export type OrgSubscriptionAction = 'allow' | 'select_plan' | 'expired';

export type BillingChannel = 'stripe' | 'wix' | 'shopify' | null;

export type OrgSubscriptionFields = {
  activeSubscription?: boolean;
  stripeCustomerId?: string | null;
  wixBillingInstanceId?: string | null;
  shopifyBillingShop?: string | null;
  subscriptionTier?: unknown | null;
  deactivateDate?: string | null;
};

/**
 * Determines how the UI should treat an org's subscription state.
 * - allow: active subscription — proceed into the app
 * - select_plan: no plan chosen yet — route to plan picker
 * - expired: had a plan that lapsed — show renewal messaging
 */
export function getOrgSubscriptionAction(
  org?: OrgSubscriptionFields | null,
): OrgSubscriptionAction {
  if (!org) {
    return 'select_plan';
  }

  if (org.activeSubscription) {
    return 'allow';
  }

  const hasBillingAccount = Boolean(
    org.stripeCustomerId ||
      org.wixBillingInstanceId ||
      org.shopifyBillingShop,
  );

  if (!hasBillingAccount) {
    return 'select_plan';
  }

  // Linked to billing but never completed plan selection
  if (!org.subscriptionTier) {
    return 'select_plan';
  }

  return 'expired';
}

export function isWixOrg(org?: OrgSubscriptionFields | null): boolean {
  return Boolean(org?.wixBillingInstanceId);
}

export function isShopifyOrg(org?: OrgSubscriptionFields | null): boolean {
  return Boolean(org?.shopifyBillingShop);
}

export function getBillingChannel(
  org?: OrgSubscriptionFields | null,
): BillingChannel {
  if (!org) {
    return null;
  }
  if (isWixOrg(org)) {
    return 'wix';
  }
  if (isShopifyOrg(org)) {
    return 'shopify';
  }
  if (org.stripeCustomerId) {
    return 'stripe';
  }
  return null;
}
